import React, { useState, useEffect, useRef } from "react";
import { SiteWide, WebLink, Images } from "../customComponents";
import Section from "../layouts/Section";
import { notify, money, decimalCount, moneyStatus } from '../../utility/Helper1';
import { depositActions } from '../../redux/actions';
import { useSelector, useDispatch } from 'react-redux';
import { allDepositSelect, userSelect, allRemoteSettingsSelect, homeScreenStatSelect } from '../../redux/selectors/index';
import { useNavigate } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Countdown from 'react-countdown';

// MUI Icons
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import HistoryIcon from "@mui/icons-material/History";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import QrCodeIcon from "@mui/icons-material/QrCode";
import SendIcon from "@mui/icons-material/Send";


const QUICK_AMOUNTS = [20, 50, 100, 200, 300, 500, 700, 1000, 2000, 5000];

// ─── Reusable card base ───────────────────────────────────────────────────────
const card = {
    background: "#ffffff",
    border: "1px solid rgba(59,130,246,0.08)",
    boxShadow: "0 4px 18px rgba(59,130,246,0.05)",
    borderRadius: 20,
    padding: 20,
};

const label = {
    color: "#64748b", fontSize: 11, fontFamily: "'DM Sans',sans-serif",
    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, display: "block",
};

const inputBase = {
    width: "100%", boxSizing: "border-box",
    padding: "13px 16px", borderRadius: 14,
    border: "1.5px solid rgba(59,130,246,0.18)",
    background: "#f8fafc", color: "#0f172a",
    fontSize: 15, fontFamily: "'DM Sans',sans-serif",
    outline: "none",
};

const CopyBtn = ({ text }) => (
    <CopyToClipboard text={text} onCopy={() => notify('Copied!', 1)}>
        <button style={{
            display: "flex", alignItems: "center", gap: 4,
            padding: "5px 12px", borderRadius: 8, border: "1.5px solid rgba(59,130,246,0.18)",
            background: "rgba(59,130,246,0.05)", cursor: "pointer", outline: "none",
            color: "#2563eb", fontSize: 11, fontFamily: "'DM Sans',sans-serif", fontWeight: 600,
            flexShrink: 0,
        }}>
            <ContentCopyIcon style={{ fontSize: 12 }} /> Copy
        </button>
    </CopyToClipboard>
);

// ─── StatusBadge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
    const cfg = {
        0: { label: "Pending", bg: "rgba(245,158,11,0.1)", border: "rgba(245,158,11,0.3)", color: "#b45309" },
        1: { label: "Processing", bg: "rgba(59,130,246,0.1)", border: "rgba(59,130,246,0.3)", color: "#1d4ed8" },
        2: { label: "Confirmed", bg: "rgba(34,197,94,0.1)", border: "rgba(34,197,94,0.3)", color: "#15803d" },
    };
    const s = cfg[status] || cfg[0];
    return (
        <span style={{
            padding: "4px 12px", borderRadius: 20, border: `1.5px solid ${s.border}`,
            background: s.bg, color: s.color, fontSize: 11, fontWeight: 700,
            fontFamily: "'DM Sans',sans-serif",
        }}>{s.label}</span>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
function DepositPage() {
    const userData = useSelector(userSelect);
    const site_settings = useSelector(allRemoteSettingsSelect);
    const finance = useSelector(homeScreenStatSelect);
    const allDepositData = useSelector(allDepositSelect);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const uploadRef = useRef(null);

    const [loadingText, setLoadingText] = useState('Processing...');
    const [loading, setLoading] = useState(false);
    const [currencySelected, setCurrency] = useState(finance?.[0]);
    const [amount, setAmount] = useState('');
    const [planSelected, setPlanSelected] = useState(null);

    const isNaira = String(currencySelected?.type) === '141' || String(currencySelected?.abbr).toUpperCase() === 'NGN';
    const defaultQuickAmounts = isNaira
        ? [10000, 25000, 50000, 100000, 200000, 300000, 500000, 1000000]
        : [20, 50, 100, 200, 300, 500, 700, 1000, 2000, 5000];

    const quickAmountsParsed = currencySelected?.deposit_packages
        ? currencySelected.deposit_packages.split(',')
            .map(item => parseFloat(item.trim()))
            .filter(val => !isNaN(val) && val > 0)
        : [];
    const quickAmounts = quickAmountsParsed.length > 0 ? quickAmountsParsed : defaultQuickAmounts;

    useEffect(() => {
        if (finance && finance.length > 0) setCurrency(finance[0]);
    }, [finance]);

    useEffect(() => {
        if (currencySelected && site_settings?.plans) {
            const activePlanGroup = site_settings.plans.find(
                (group) => Number(group.mother.type) === Number(currencySelected.type)
            );
            const plans = activePlanGroup?.childs || [];
            if (plans.length > 0) {
                setPlanSelected(plans[0]);
            } else {
                setPlanSelected(null);
            }
        }
    }, [currencySelected, site_settings]);

    const activePlanGroup = site_settings?.plans?.find(
        (group) => Number(group.mother.type) === Number(currencySelected?.type)
    );
    const plansForSelectedCurrency = activePlanGroup?.childs || [];

    useEffect(() => {
        let timing;
        const poll = () => {
            dispatch(depositActions.onLoadAllDeposits("deposit/load", () => {
                const hasPending = allDepositData?.some(i => Number(i.status) < 2);
                if (hasPending) {
                    timing = setTimeout(poll, 10000);
                } else {
                    timing = setTimeout(poll, 30000);
                }
            }));
        };

        poll();
        return () => clearTimeout(timing);
    }, [allDepositData?.length, dispatch]);

    const getPendingPayment = allDepositData?.filter(i => Number(i.status) < 2);
    const hasPending = (getPendingPayment?.length ?? 0) > 0;

    // ── Upload handler ──────────────────────────────────────────────────────
    const uploadSelectedPayment = (file) => {
        setLoadingText('Uploading receipt...');
        setLoading(true);
        dispatch(depositActions.onCreateDeposit(
            { txn_id: getPendingPayment?.[0]?.txn_id, image: file },
            "deposit/manual-deposit-upload",
            response => { setLoading(false); notify(response.msg, response.code); }
        ));
    };

    // ── Delete handler ──────────────────────────────────────────────────────
    const deleteDeposit = (txn_id) => {
        if (!window.confirm('Delete this payment request?')) return;
        setLoading(true);
        setLoadingText('Deleting...');
        dispatch(depositActions.onLoadAllDeposits(`deposit/delete/${txn_id}`, response => {
            setLoading(false);
            notify(response.msg, response.code);
        }));
    };

    // ── Submit deposit ──────────────────────────────────────────────────────
    const processDeposit = (e) => {
        e.preventDefault();
        if (isNaN(amount) || !amount || parseFloat(amount) <= 0) {
            notify('Enter a valid amount', 0); return;
        }
        const minLimit = planSelected ? parseFloat(planSelected.minimumParticipation) : parseFloat(currencySelected?.minimumDeposit || 0);
        const maxLimit = planSelected ? parseFloat(planSelected.maximumParticipation) : parseFloat(currencySelected?.maximumDeposit || 999999999);
        const inputAmt = parseFloat(amount);
        if (inputAmt < minLimit) {
            notify(`Minimum deposit amount is ${minLimit}`, 0); return;
        }
        if (inputAmt > maxLimit) {
            notify(`Maximum deposit amount is ${maxLimit}`, 0); return;
        }
        setLoading(true);
        setLoadingText('Generating payment...');
        dispatch(depositActions.onCreateDeposit(
            { amount, currency: currencySelected.type, plan: planSelected ? planSelected.type : 0 },
            "deposit/new",
            response => {
                if (response.code == 1) setAmount('');
                else notify(response.msg, response.code);
                setLoading(false);
            }
        ));
    };

    // ═══════════════════════════════════════════════════════════════════════
    //  VIEW A — Pending payment screen
    // ═══════════════════════════════════════════════════════════════════════
    const renderPending = () => {
        const pending = getPendingPayment?.[0];
        const curDetails = finance?.filter(f => f.type == pending?.currency)?.[0];
        const displayAmt = `${pending?.amountEnforcedToPay} ${pending?.abbr}`;

        let perfectWalletobj, eachParam;
        if (pending?.createdWithAutoOrManual == 0 && pending?.autoConfirm == 2) {
            try {
                const parsed = JSON.parse(pending.address);
                eachParam = Object.keys(parsed.param);
                perfectWalletobj = parsed;
            } catch (_) { }
        }

        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 24 }}>

                {/* ── Status hero ─────────────────────────────────────── */}
                <div style={{
                    borderRadius: 22, padding: "22px 20px", position: "relative", overflow: "hidden",
                    background: "linear-gradient(135deg,#1e3a8a 0%,#2563eb 55%,#3b82f6 100%)",
                    boxShadow: "0 10px 28px rgba(37,99,235,0.3)",
                }}>
                    <div style={{
                        position: "absolute", top: -30, right: -30, width: 160, height: 160,
                        borderRadius: "50%", background: "radial-gradient(circle,rgba(147,197,253,0.22),transparent)",
                        pointerEvents: "none",
                    }} />
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                        <div>
                            <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>
                                Deposit Request
                            </p>
                            <p style={{ color: "#fff", fontSize: 26, fontWeight: 800, fontFamily: "'Syne',sans-serif", margin: "6px 0 0", lineHeight: 1.1 }}>
                                {displayAmt}
                            </p>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                                <StatusBadge status={pending?.status} />
                                {Number(pending?.status) === 1 && pending?.blockConfirmationsAchieved !== undefined && (
                                    <span style={{ fontSize: 12, color: "rgba(255,255,255,0.9)", fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>
                                        ({pending.blockConfirmationsAchieved} Block Confirmation{Number(pending.blockConfirmationsAchieved) !== 1 ? 's' : ''})
                                    </span>
                                )}
                            </div>
                        </div>
                        <div style={{
                            width: 48, height: 48, borderRadius: "50%",
                            background: "rgba(255,255,255,0.15)", border: "1.5px solid rgba(255,255,255,0.3)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <AccountBalanceWalletIcon style={{ color: "#fff", fontSize: 24 }} />
                        </div>
                    </div>
                    {/* Txn ID */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14 }}>
                        <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 11, fontFamily: "'DM Sans',sans-serif", fontFamily: "monospace" }}>
                            {pending?.txn_id}
                        </span>
                        <CopyToClipboard text={pending?.txn_id} onCopy={() => notify('ID copied', 1)}>
                            <button style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 6, padding: "3px 8px", cursor: "pointer", outline: "none" }}>
                                <ContentCopyIcon style={{ color: "#fff", fontSize: 12 }} />
                            </button>
                        </CopyToClipboard>
                    </div>
                </div>

                {/* ── Amount to pay ─────────────────────────────────── */}
                <div style={card}>
                    <span style={label}>Amount to Pay</span>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <p style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#0f172a", fontFamily: "'Syne',sans-serif" }}>{displayAmt}</p>
                        <CopyBtn text={pending?.amountEnforcedToPay} />
                    </div>
                </div>

                {/* ── Countdown timer ───────────────────────────────── */}
                {pending?.timeout && (
                    <div style={{ ...card, background: "linear-gradient(135deg,rgba(245,158,11,0.06),rgba(245,158,11,0.02))", border: "1px solid rgba(245,158,11,0.2)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                            <AccessTimeIcon style={{ color: "#b45309", fontSize: 16 }} />
                            <span style={{ ...label, margin: 0, color: "#b45309" }}>Payment Expires In</span>
                        </div>
                        <Countdown
                            date={new Date(pending.timeout)}
                            renderer={({ hours, minutes, seconds, completed }) =>
                                completed
                                    ? <span style={{ color: "#ef4444", fontWeight: 700, fontFamily: "'Syne',sans-serif" }}>Payment Expired</span>
                                    : (
                                        <div style={{ display: "flex", gap: 10 }}>
                                            {[{ v: hours, l: "HR" }, { v: minutes, l: "MIN" }, { v: seconds, l: "SEC" }].map(({ v, l }) => (
                                                <div key={l} style={{ textAlign: "center", minWidth: 52, background: "#fff", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 12, padding: "8px 0" }}>
                                                    <p style={{ margin: 0, fontSize: 20, fontWeight: 800, fontFamily: "'Syne',sans-serif", color: "#b45309" }}>
                                                        {String(v).padStart(2, '0')}
                                                    </p>
                                                    <p style={{ margin: 0, fontSize: 9, color: "#94a3b8", fontFamily: "'DM Sans',sans-serif" }}>{l}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )
                            }
                        />
                    </div>
                )}

                {/* ── Crypto payment details ────────────────────────── */}
                {pending?.autoConfirm == 1 && pending?.address && (
                    <div style={card}>
                        <span style={label}>Deposit Address</span>
                        <div style={{
                            display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between",
                            background: "var(--neutral-100)", border: "1.5px solid var(--border-color)",
                            borderRadius: 12, padding: "12px 14px",
                        }}>
                            <span style={{ color: "var(--text-main)", fontSize: 12, fontFamily: "monospace", wordBreak: "break-all", flex: 1 }}>
                                {pending.address}
                            </span>
                            <CopyBtn text={pending.address} />
                        </div>

                        {/* QR Code */}
                        {pending.qrCode && (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 16 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                                    <QrCodeIcon style={{ fontSize: 14, color: "#64748b" }} />
                                    <span style={{ ...label, margin: 0 }}>Scan QR Code</span>
                                </div>
                                <div style={{
                                    "--qr-bg": "#ffffff",
                                    background: "var(--qr-bg)",
                                    border: "1px solid var(--border-color)",
                                    borderRadius: 16, padding: 12, display: "inline-block",
                                }}>
                                    <img src={pending.qrCode} alt="QR" style={{ width: 200, height: 200, display: "block" }} />
                                </div>
                            </div>
                        )}

                        {/* Instruction chip */}
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 14, padding: "10px 14px", background: "rgba(59,130,246,0.05)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 12 }}>
                            <InfoOutlinedIcon style={{ fontSize: 14, color: "#2563eb", flexShrink: 0, marginTop: 1 }} />
                            <p style={{ margin: 0, fontSize: 12, color: "#334155", fontFamily: "'DM Sans',sans-serif", lineHeight: 1.5 }}>
                                Send exactly <strong>{displayAmt}</strong> to the address above. Only send <strong>{pending?.abbr}</strong> to this wallet.
                                {pending?.paymentMessage && <><br /><span style={{ color: "#ef4444" }}>{pending.paymentMessage}</span></>}
                            </p>
                        </div>
                    </div>
                )}

                {/* ── Perfect Money / auto form ──────────────────────── */}
                {pending?.createdWithAutoOrManual == 0 && pending?.autoConfirm == 2 && perfectWalletobj && (
                    <div style={card}>
                        <form action={perfectWalletobj.form_url} method="POST">
                            {eachParam.map(k => (
                                <input key={k} name={k} value={perfectWalletobj.param[k]} type="hidden" />
                            ))}
                            <button type="submit" style={{
                                width: "100%", padding: "14px 0", borderRadius: 14, border: "none",
                                background: "linear-gradient(135deg,#16a34a,#22c55e)",
                                color: "#fff", fontSize: 15, fontWeight: 700, fontFamily: "'Syne',sans-serif",
                                cursor: "pointer", boxShadow: "0 4px 14px rgba(22,163,74,0.25)",
                            }}>
                                Pay Now
                            </button>
                        </form>
                    </div>
                )}

                {/* ── Manual upload receipt ─────────────────────────── */}
                {pending?.createdWithAutoOrManual == 1 && (
                    <div style={{ ...card, textAlign: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 14 }}>
                            <UploadFileIcon style={{ color: "#2563eb", fontSize: 20 }} />
                            <span style={{ color: "#0f172a", fontSize: 14, fontWeight: 700, fontFamily: "'Syne',sans-serif" }}>
                                Payment Receipt
                            </span>
                        </div>
                        <button
                            onClick={() => uploadRef.current?.click()}
                            style={{
                                padding: "12px 24px", borderRadius: 12, border: "none",
                                background: "linear-gradient(135deg,#1e3a8a,#2563eb)",
                                color: "#fff", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans',sans-serif",
                                cursor: "pointer", boxShadow: "0 4px 14px rgba(37,99,235,0.25)",
                                display: "inline-flex", alignItems: "center", gap: 8,
                            }}
                        >
                            <UploadFileIcon style={{ fontSize: 16 }} />
                            {pending?.image == null ? 'Upload Receipt' : 'Re-upload Receipt'}
                        </button>
                        <input
                            ref={uploadRef}
                            style={{ display: "none" }}
                            onChange={e => uploadSelectedPayment(e.target.files[0])}
                            type="file" accept=".jpg,.gif,.png,.jpeg"
                        />
                    </div>
                )}

                {/* ── Bank transfer details ─────────────────────────── */}
                {pending?.autoConfirm == 0 && (
                    <div style={card}>
                        <span style={label}>Bank Transfer Details</span>
                        {[
                            { key: curDetails?.accountNameText, val: curDetails?.accountName },
                            { key: curDetails?.accountNumberText, val: curDetails?.accountNumber, copy: true },
                            { key: "Bank", val: curDetails?.bankName },
                        ].filter(r => r.val).map(row => (
                            <div key={row.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, padding: "10px 14px", background: "var(--neutral-100)", border: "1px solid var(--border-color)", borderRadius: 12 }}>
                                <div>
                                    <p style={{ margin: 0, fontSize: 10, color: "var(--text-muted)", fontFamily: "'DM Sans',sans-serif", textTransform: "uppercase", letterSpacing: "0.04em" }}>{row.key}</p>
                                    <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "var(--text-main)", fontFamily: "'Syne',sans-serif", marginTop: 2 }}>{row.val}</p>
                                </div>
                                {row.copy && <CopyBtn text={row.val} />}
                            </div>
                        ))}
                    </div>
                )}

                {/* ── Delete button ─────────────────────────────────── */}
                {pending?.status == 0 && (
                    <button
                        onClick={() => deleteDeposit(pending?.txn_id)}
                        style={{
                            width: "100%", padding: "13px 0", borderRadius: 14,
                            border: "1.5px solid rgba(239,68,68,0.25)", background: "rgba(239,68,68,0.04)",
                            color: "#ef4444", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans',sans-serif",
                            cursor: "pointer", outline: "none",
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                        }}
                    >
                        <DeleteOutlineIcon style={{ fontSize: 17 }} />
                        Cancel & Delete Request
                    </button>
                )}

            </div>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════
    //  VIEW B — New deposit form
    // ═══════════════════════════════════════════════════════════════════════
    const renderForm = () => (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 24 }}>

            {/* ── Hero card ──────────────────────────────────────────── */}
            <div style={{
                borderRadius: 22, padding: "22px 20px", position: "relative", overflow: "hidden",
                background: "linear-gradient(135deg,#1e3a8a 0%,#2563eb 55%,#3b82f6 100%)",
                boxShadow: "0 10px 28px rgba(37,99,235,0.3)",
            }}>
                <div style={{
                    position: "absolute", top: -30, right: -30, width: 160, height: 160,
                    borderRadius: "50%", background: "radial-gradient(circle,rgba(147,197,253,0.22),transparent)",
                    pointerEvents: "none",
                }} />
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                        <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>
                            Current Balance
                        </p>
                        <p style={{ color: "#fff", fontSize: 30, fontWeight: 800, fontFamily: "'Syne',sans-serif", margin: "6px 0 0", lineHeight: 1.1 }}>
                            {money(currencySelected?.balance ?? 0, currencySelected?.type)}
                        </p>
                    </div>
                    <button
                        onClick={() => navigate('/Deposit-records')}
                        style={{
                            width: 48, height: 48, borderRadius: "50%",
                            background: "rgba(255,255,255,0.15)", border: "1.5px solid rgba(255,255,255,0.3)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: "pointer", outline: "none", transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.25)"; e.currentTarget.style.transform = "scale(1.05)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; e.currentTarget.style.transform = "scale(1)"; }}
                    >
                        <ChevronRightIcon style={{ color: "#fff", fontSize: 24 }} />
                    </button>
                </div>
            </div>

            {/* Currency picker removed in profile switch update */}

            {/* ── Chat Plan Picker ────────────────────────────────────── */}
            {plansForSelectedCurrency && plansForSelectedCurrency.length > 0 && (
                <div style={card}>
                    <span style={label}>Conversation Manager</span>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {plansForSelectedCurrency.map(plan => {
                            const active = planSelected?.type === plan.type;
                            return (
                                <button
                                    key={plan.type}
                                    onClick={() => setPlanSelected(plan)}
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        justifyContent: "center",
                                        width: "100%",
                                        padding: "14px 18px",
                                        borderRadius: 16,
                                        cursor: "pointer",
                                        outline: "none",
                                        background: active
                                            ? "linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(59, 130, 246, 0.03) 100%)"
                                            : "rgba(59, 130, 246, 0.02)",
                                        border: active
                                            ? "2px solid #2563eb"
                                            : "1.5px solid rgba(59, 130, 246, 0.08)",
                                        boxShadow: active ? "0 4px 20px rgba(37, 99, 235, 0.08)" : "none",
                                        transition: "all 0.2s ease-in-out",
                                        position: "relative",
                                        overflow: "hidden",
                                        textAlign: "left"
                                    }}
                                >
                                    {active && (
                                        <div style={{
                                            position: "absolute",
                                            top: 0,
                                            right: 0,
                                            background: "#2563eb",
                                            color: "#ffffff",
                                            padding: "3px 12px",
                                            borderBottomLeftRadius: 12,
                                            fontSize: 10,
                                            fontWeight: 800,
                                            textTransform: "uppercase",
                                            fontFamily: "'DM Sans', sans-serif"
                                        }}>
                                            Selected
                                        </div>
                                    )}
                                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                                        <span style={{
                                            color: active ? "#1e3a8a" : "var(--text-main)",
                                            fontSize: 15,
                                            fontWeight: 700,
                                            fontFamily: "'Syne', sans-serif"
                                        }}>
                                            {plan.name}
                                        </span>
                                        <span style={{
                                            color: "#16a34a",
                                            fontSize: 15,
                                            fontWeight: 800,
                                            fontFamily: "'Syne', sans-serif"
                                        }}>
                                            {plan.chattyCenterDailyRoi}% Daily
                                        </span>
                                    </div>
                                    <div style={{
                                        display: "flex",
                                        gap: 12,
                                        marginTop: 6,
                                        fontSize: 11,
                                        color: "var(--text-muted)",
                                        fontFamily: "'DM Sans', sans-serif"
                                    }}>
                                        <span>Tenure: <strong>{plan.chattyCenterTenureDays} Days</strong></span>
                                        <span>•</span>
                                        <span>Min Limit: <strong>{money(plan.minimumParticipation, currencySelected?.type)}</strong></span>
                                        <span>•</span>
                                        <span>Max Limit: <strong>{money(plan.maximumParticipation, currencySelected?.type)}</strong></span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Amount input ─────────────────────────────────────────── */}
            <div style={card}>
                <span style={label}>Deposit Amount</span>
                <div style={{ position: "relative" }}>
                    <input
                        type="number"
                        onChange={(e) => setAmount(e.target.value)}
                        value={amount}
                        placeholder="0.00"
                        style={{ ...inputBase, paddingRight: 60, fontSize: 22, fontWeight: 700, cursor: "default" }}
                    />
                    <span style={{
                        position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)",
                        color: "#64748b", fontSize: 12, fontFamily: "'DM Sans',sans-serif", fontWeight: 600,
                    }}>
                        {currencySelected?.abbr || currencySelected?.symbol}
                    </span>
                </div>
                {/* Quick picks */}
                <div style={{
                    display: "flex", gap: 8, flexWrap: "nowrap",
                    overflowX: "auto", marginTop: 12,
                    paddingBottom: 4,
                    msOverflowStyle: "none", scrollbarWidth: "none",
                }}>
                    {quickAmounts.map(n => (
                        <button key={n} type="button" onClick={() => setAmount(n.toString())} style={{
                            padding: "5px 13px", borderRadius: 20, cursor: "pointer", outline: "none",
                            background: amount == n ? "rgba(37,99,235,0.1)" : "rgba(59,130,246,0.04)",
                            border: amount == n ? "1.5px solid rgba(37,99,235,0.3)" : "1.5px solid rgba(59,130,246,0.1)",
                            color: amount == n ? "#1d4ed8" : "#64748b",
                            fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans',sans-serif",
                        }}>
                            {n}
                        </button>
                    ))}
                </div>

            </div>

            {/* ── Currency deposit note ──────────────────────────────── */}
            {(currencySelected?.deposit_note_head || currencySelected?.deposit_note_body) && (
                <div style={{
                    ...card,
                    background: "rgba(59,130,246,0.04)", border: "1px solid rgba(59,130,246,0.12)",
                }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        <InfoOutlinedIcon style={{ color: "#2563eb", fontSize: 18, flexShrink: 0, marginTop: 2 }} />
                        <div>
                            {currencySelected.deposit_note_head && (
                                <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 700, color: "var(--text-main)", fontFamily: "'Syne',sans-serif" }}>
                                    {currencySelected.deposit_note_head}
                                </p>
                            )}
                            {currencySelected.deposit_note_body && (
                                <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)", fontFamily: "'DM Sans',sans-serif", lineHeight: 1.6 }}>
                                    {currencySelected.deposit_note_body}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Confirmation summary ──────────────────────────────── */}
            {amount && parseFloat(amount) > 0 && (
                <div style={{
                    ...card,
                    background: "linear-gradient(135deg,rgba(34,197,94,0.06),rgba(34,197,94,0.02))",
                    border: "1px solid rgba(34,197,94,0.18)",
                }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ color: "var(--text-muted)", fontSize: 12, fontFamily: "'DM Sans',sans-serif" }}>You are depositing</span>
                        <span style={{ color: "var(--text-main)", fontSize: 16, fontWeight: 800, fontFamily: "'Syne',sans-serif" }}>
                            {parseFloat(amount).toFixed(2)} {currencySelected?.abbr || ""}
                        </span>
                    </div>
                    {planSelected && (
                        <>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                                <span style={{ color: "var(--text-muted)", fontSize: 12, fontFamily: "'DM Sans',sans-serif" }}>Selected Plan</span>
                                <span style={{ color: "#2563eb", fontSize: 13, fontWeight: 700, fontFamily: "'Syne',sans-serif" }}>
                                    {planSelected.name} ({planSelected.chattyCenterDailyRoi}% Daily)
                                </span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
                                <span style={{ color: "var(--text-muted)", fontSize: 12, fontFamily: "'DM Sans',sans-serif" }}>Duration</span>
                                <span style={{ color: "var(--text-main)", fontSize: 13, fontWeight: 700, fontFamily: "'DM Sans',sans-serif" }}>
                                    {planSelected.chattyCenterTenureDays} Days
                                </span>
                            </div>
                        </>
                    )}
                    <div style={{ height: 1, background: "rgba(34,197,94,0.15)", margin: "10px 0" }} />
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <CheckCircleOutlineIcon style={{ fontSize: 13, color: "#22c55e" }} />
                        <span style={{ color: "#16a34a", fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>
                            Chat opens immediately after payment confirmation
                        </span>
                    </div>
                </div>
            )}

            {/* ── Submit ────────────────────────────────────────────── */}
            <button
                onClick={processDeposit}
                disabled={loading}
                style={{
                    width: "100%", padding: "16px 0", borderRadius: 16, border: "none",
                    background: loading ? "rgba(59,130,246,0.3)" : "linear-gradient(135deg,#1e3a8a,#2563eb)",
                    color: "#fff", fontSize: 15, fontWeight: 700, fontFamily: "'Syne',sans-serif",
                    cursor: loading ? "not-allowed" : "pointer", outline: "none",
                    boxShadow: loading ? "none" : "0 6px 20px rgba(37,99,235,0.3)",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
            >
                {loading
                    ? <><div style={{ width: 18, height: 18, border: "2.5px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />Processing...</>
                    : <><SendIcon style={{ fontSize: 18 }} /> Generate Payment</>
                }
            </button>

        </div>
    );

    return (
        <SiteWide id="main" title="Deposit">
            <Section overlayText={loadingText} loading={loading} title="Fund Wallet">
                {hasPending ? renderPending() : renderForm()}
                <style>{`@keyframes spin { to { transform: rotate(360deg); } } .chips-scroll::-webkit-scrollbar { display: none; }`}</style>
            </Section>
        </SiteWide>
    );
}

export default DepositPage;
